import React, { useId } from 'react'
import { type OTPInputProps, REGEXP_ONLY_DIGITS } from 'input-otp'
import { Input } from './ui/input.tsx'
import { Textarea } from './ui/textarea.tsx'
import classNames from 'classnames'
import { InputNoCustomClassNames } from '#app/components/ui/input-no-classnames.tsx'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { Link } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from './ui/input-otp.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function CheckboxField({
	name = 'default-checkbox-name',
	label = 'Default label',
	errors = [],
	isChecked = false,
	className = '',
	labelClassName = '',
	checkboxClassName = '',
}) {
	return (
		<div className={`checkbox-field ${className}`}>
			<div className="flex gap-2">
				<input
					type="checkbox"
					id={name}
					name={name}
					className={`h-7 w-7 shrink-0  ${checkboxClassName}`}
					defaultChecked={isChecked}
				/>
				<label htmlFor={name} className={classNames('text-lg', labelClassName)}>
					{label}
				</label>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errors.length > 0 && (
					<ul>
						{errors.map((error, index) => (
							<li key={index}>{error}</li>
						))}
					</ul>
				)}
			</div>
		</div>
	)
}

export function Error({ message }: { message: string }) {
	if (!message?.length) return null
	return (
		<div className="flex flex-col gap-1 text-center">
			<div className="text-[14px] text-foreground-destructive">{message}</div>
		</div>
	)
}

export function ErrorList({ errors }: { errors?: ListOfErrors }) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<div className="mb-4 flex flex-col gap-1 text-center">
			{errorsToRender.map(e => (
				<div key={e} className="text-[14px] text-foreground-destructive">
					{e}
				</div>
			))}
		</div>
	)
}

export function ErrorBulletList({ id, errors }: { id?: string; errors?: ListOfErrors }) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="mt-2 flex flex-col gap-1 text-center">
			{errorsToRender.map(e => (
				<li key={e} className="text-[15px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function DateField({
	labelProps,
	inputProps,
	errors,
	onChange,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	onChange?: any
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<label
				className="text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				htmlFor={id}
				{...labelProps}
			/>
			<Input
				id={id}
				type="date"
				onChange={onChange}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorBulletList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function SelectField({
	labelProps,
	selectProps,
	onChange,
	options,
	errors,
	className,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	selectProps: any
	onChange?: any
	options: any
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = selectProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div>
			<label
				className={classNames(
					'text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
					className,
					'text-sm',
				)}
				htmlFor={id}
				{...labelProps}
			/>
			<select
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				onChange={onChange}
				{...selectProps}
				className="min-h-[40px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-blue-500 focus:ring focus:ring-blue-200 sm:min-h-[48px] sm:text-base"
			>
				{options.length > 0 ? (
					options.map((option: any) => (
						// @ts-ignore
						<option key={option.value} value={option.value} disabled={option.disabled}>
							{option.label}
						</option>
					))
				) : (
					<option value="" disabled>
						No options available
					</option>
				)}
			</select>
			<div className="min-h-[24px]">
				{errorId && (
					<div className="px-3 pb-2 pt-1 text-xs sm:min-h-[32px] sm:px-4 sm:pb-3 sm:text-sm">
						<ErrorBulletList id={errorId} errors={errors} />
					</div>
				)}
			</div>
		</div>
	)
}

export function SaveMessageField({
	id,
	labelProps,
	inputProps,
}: {
	id: string
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
}) {
	return (
		<>
			<label
				className="text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				htmlFor={id}
				{...labelProps}
			/>
			<InputNoCustomClassNames id={id} aria-invalid={id ? true : undefined} aria-describedby={id} {...inputProps} />
		</>
	)
}

export function PasswordField({
	inputProps,
	errors,
	showPassword,
	togglePasswordVisibility,
	generateStrongPassword,
	createPassword,
	label,
}: {
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
	showPassword: boolean
	togglePasswordVisibility: any
	generateStrongPassword?: any
	createPassword?: boolean
	label?: string
}) {
	const { t } = useTranslation()
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className="flex flex-col">
			<label className="mb-1 block text-sm text-gray-700" htmlFor="password">
				{label ? label : t('root.login.password')}
			</label>
			{createPassword && (
				<div className="mb-1 block whitespace-nowrap text-xs text-gray-500">
					{t('root.login.passwordHint')}
				</div>
			)}
			<div className="relative flex items-center">
				<Input
					className="h-14 w-full rounded-lg border-gray-300 bg-gray-100 px-3 py-2 text-black shadow"
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...inputProps}
					type={showPassword ? 'text' : 'password'}
				/>
				<button
					type="button"
					onClick={togglePasswordVisibility}
					className="absolute right-3 text-gray-400 focus:outline-none"
				>
					{showPassword ? <FaEyeSlash /> : <FaEye />}
				</button>
			</div>
			{createPassword ? (
				<div className="text-sm text-blue-400">
					<Link to={'#'} onClick={() => generateStrongPassword()}>
						{t('root.login.suggestStrongPassword')}
					</Link>
				</div>
			) : (
				<div></div>
			)}
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorBulletList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<label
				className="text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				htmlFor={id}
				{...labelProps}
			/>
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			<div className="min-h-[32px] px-4 pb-3 pt-2">
				{errorId ? <ErrorBulletList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<label
				className="text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				htmlFor={id}
				{...labelProps}
			/>
			<Input id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...inputProps} />
			<div className="min-h-[24px] px-4 pb-3 pt-1">
				{errorId ? <ErrorBulletList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<label
				className="text-sm font-medium leading-none text-gray-400 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				htmlFor={id}
				{...labelProps}
			/>
			<Textarea id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...textareaProps} />
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorBulletList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}
